import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getClientTasks, updateTask, getOneClient, getAllEmployees } from "../../../redux/actions/authActions";
import { IoIosArrowBack } from "react-icons/io";
import { VscCloudUpload, VscTrash, VscCloudDownload } from "react-icons/vsc";
import { AiOutlineEye, AiOutlinePlus } from "react-icons/ai";
import { Modal, message, Select } from "antd";

const { Option } = Select;

const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry"
];

const ViewTaskHome = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get("taskId");
  const clientId = searchParams.get("clientId");

  const clientTasks = useSelector((state) => state.auth.clientTasks);
  const client = useSelector((state) => state.auth.client);
  const team = useSelector((state) => state.auth.allEmployees);

  const [task, setTask] = useState({});
  const [description, setDescription] = useState("");
  const [assignee, setAssignee] = useState(""); // eslint-disable-next-line
  const [clientRemarks, setClientRemarks] = useState(""); 
  const [locations, setLocations] = useState([
    { state: "", location: "" }
  ]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [fileType, setFileType] = useState("");

  const handleLocationChange = (index, field, value) => {
    const updated = [...locations];
    updated[index][field] = value;
    setLocations(updated);
  };

  const handleAddLocation = () => {
    setLocations([...locations, { state: "", location: "" }]);
  };

  const handleRemoveLocation = (index) => {
    if (locations.length > 1) {
      setLocations(locations.filter((_, i) => i !== index));
    } else {
      setLocations([{ state: "", location: "" }]);
    }
  };

  useEffect(() => {
    dispatch(getClientTasks(clientId));
    dispatch(getAllEmployees());
    if (!client?._id) {
      dispatch(getOneClient(clientId));
    }
  }, [clientId, dispatch, client?._id]);

  useEffect(() => {
    const selectedTask = clientTasks.find((t) => t._id === taskId);
    if (selectedTask) {
      setTask(selectedTask);
      setDescription(selectedTask.description || "");
      setAssignee(selectedTask.assignees?.[0]?._id || "");
      setClientRemarks(selectedTask.clientRemarks || ""); 

      if (selectedTask.locations && Array.isArray(selectedTask.locations) && selectedTask.locations.length > 0) {
        setLocations(selectedTask.locations.map((l) => ({
          state: l.state || "",
          location: l.location || "",
        })));
      } else if (selectedTask.state || selectedTask.location) {
        setLocations([
          { state: selectedTask.state || "", location: selectedTask.location || "" }
        ]);
      } else {
        setLocations([{ state: "", location: "" }]);
      }

      const freshDocs = [];
      if (selectedTask.documents && selectedTask.documents.length > 0) {
        selectedTask.documents.forEach((d) => {
          freshDocs.push({ file: null, name: d.name, url: d.url });
        });
      } 
      else if (selectedTask.document && typeof selectedTask.document === "string") {
        const urlParts = selectedTask.document.split("/");
        const fileName = urlParts[urlParts.length - 1].split("-").slice(1).join("-") || urlParts[urlParts.length - 1];
        freshDocs.push({ file: null, name: fileName, url: selectedTask.document });
      }
      setUploadedFiles(freshDocs);
    }
  }, [clientTasks, taskId]);

  const handleUpload = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map((file) => ({
      file,
      name: file.name,
      url: URL.createObjectURL(file),
    }));
    setUploadedFiles((prev) => [...prev, ...newFiles]);
  };

  const handleDelete = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      const validLocations = locations.filter(
        (l) => (l.state && l.state.trim()) || (l.location && l.location.trim())
      );

      const finalFormData = new FormData();
      finalFormData.append("description", description);
      finalFormData.append("assignees", assignee);
      finalFormData.append("locations", JSON.stringify(validLocations));

      const uniqueStates = [...new Set(validLocations.map((l) => l.state?.trim()).filter(Boolean))];
      finalFormData.append("state", uniqueStates.join(", "));

      const locSummaries = validLocations
        .filter((l) => l.location?.trim() || l.state?.trim())
        .map((l) => {
          const locName = l.location?.trim() || "";
          const stName = l.state?.trim() || "";
          if (locName && stName) return `${locName} (${stName})`;
          return locName || stName;
        });
      finalFormData.append("location", locSummaries.join(", "));

      const prevTaskDocuments = [];
      uploadedFiles.forEach((f) => {
        if (f.file) {
          finalFormData.append("taskDocuments", f.file);
        } else {
          prevTaskDocuments.push({ name: f.name, url: f.url });
        }
      });

      finalFormData.append("prevTaskDocuments", JSON.stringify(prevTaskDocuments));

      // This call hits the Admin update controller which now changes 'description', 'documents', and 'assignees'
      await dispatch(updateTask(finalFormData, taskId));
      message.success("Task updated successfully");
      
      await dispatch(getClientTasks(clientId));
      navigate(-1);
    } catch (err) {
      console.error("Save Error:", err);
      message.error("Failed to update task");
    }
  };

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Task Management</h1>
          <button onClick={() => navigate(-1)} className="flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-all shadow-sm">
            <IoIosArrowBack />
            <span>Back to Client</span>
          </button>
        </div>

        {client && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-100">
            <div className="flex justify-between items-start">
                <div>
                    <h2 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">Client Overview</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">Business Name</label><p className="text-gray-900 font-medium">{client.businessName || "N/A"}</p></div>
                        <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">Contact Person</label><p className="text-gray-900 font-medium">{client.name || "N/A"}</p></div>
                        <div><label className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">Email Address</label><p className="text-gray-900 font-medium">{client.email || "N/A"}</p></div>
                    </div>
                </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-lg p-8 space-y-8 border border-gray-100">
          <div className="flex justify-between items-center border-b border-gray-50 pb-4">
            <h2 className="text-2xl font-bold text-gray-800 tracking-tight">{task?.service?.name || "Edit Task"}</h2>
            <span className="px-4 py-1 bg-indigo-50 text-indigo-600 rounded-full text-xs font-bold uppercase tracking-widest">
              {task.status || "Pending"}
            </span>
          </div>

          <div className="space-y-6">
            <div className="max-w-md">
              <label className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 block">Assigned Team Member</label>
              <Select
                className="w-full h-12"
                placeholder="Select Team Member"
                value={assignee}
                onChange={(val) => setAssignee(val)}
              >
                {team?.map((member) => (
                  <Option key={member._id} value={member._id}>{member.name}</Option>
                ))}
              </Select>
            </div>

            {/* Dynamic Branches / Locations */}
            <div className="p-5 bg-gray-50 border border-gray-200 rounded-xl">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block">
                    Branches / Locations ({locations.length})
                  </label>
                  <span className="text-xs text-gray-400">Manage all branch locations for this task</span>
                </div>
                <button
                  type="button"
                  onClick={handleAddLocation}
                  className="flex items-center gap-1 text-xs font-semibold text-[#045B9B] hover:text-[#03406d] bg-blue-50 hover:bg-blue-100 border border-blue-200 px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
                >
                  <AiOutlinePlus className="text-sm" /> Add Branch / Location
                </button>
              </div>

              <div className="space-y-3">
                {locations.map((item, index) => (
                  <div
                    key={index}
                    className="flex flex-col sm:flex-row gap-3 items-center bg-white p-3 rounded-lg border border-gray-200 shadow-sm"
                  >
                    <div className="w-full sm:w-1/2">
                      <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">
                        State #{index + 1}
                      </label>
                      <select
                        value={item.state}
                        onChange={(e) => handleLocationChange(index, "state", e.target.value)}
                        className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm bg-gray-50 focus:ring-2 focus:ring-blue-100 focus:border-blue-400 focus:outline-none transition-all"
                      >
                        <option value="">Select State</option>
                        {INDIAN_STATES.map((st) => (
                          <option key={st} value={st}>{st}</option>
                        ))}
                      </select>
                    </div>

                    <div className="w-full sm:w-1/2">
                      <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">
                        Branch / City
                      </label>
                      <input
                        type="text"
                        value={item.location}
                        onChange={(e) => handleLocationChange(index, "location", e.target.value)}
                        className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm bg-gray-50 focus:ring-2 focus:ring-blue-100 focus:border-blue-400 focus:outline-none transition-all"
                        placeholder="e.g. Visakhapatnam, Bangalore..."
                      />
                    </div>

                    <div className="flex items-end self-end sm:self-center pt-2 sm:pt-4">
                      <button
                        type="button"
                        disabled={locations.length === 1 && !item.state && !item.location}
                        onClick={() => handleRemoveLocation(index)}
                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        title="Remove Branch"
                      >
                        <VscTrash size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Team Escalation */}
          {task?.teamDelayReason && (
            <div className="p-5 rounded-xl border-l-4 bg-red-50 border-red-400 shadow-sm">
              <label className="text-xs font-bold uppercase tracking-widest text-red-600 flex items-center gap-2">
                🚨 Team Escalation / Work Delay
              </label>
              <p className="mt-2 text-gray-700 leading-relaxed break-words whitespace-pre-wrap font-medium italic">
                "{task.teamDelayReason}"
              </p>
            </div>
          )}

          {/* Client Escalation */}
          {task?.clientDelayReason && (
            <div className="p-5 rounded-xl border-l-4 bg-orange-50 border-orange-400 shadow-sm">
              <label className="text-xs font-bold uppercase tracking-widest text-orange-600 flex items-center gap-2">
                📢 Client Escalation
              </label>
              <p className="mt-2 text-gray-700 leading-relaxed break-words whitespace-pre-wrap font-medium italic">
                "{task.clientDelayReason}"
              </p>
            </div>
          )}

          {/* Team Member Description */}
          {task?.teamRemarks && (
            <div className="p-5 rounded-xl border-l-4 bg-green-50 border-green-400 shadow-sm">
              <label className="text-xs font-bold uppercase tracking-widest text-green-600">
                Team Member Description
              </label>
              <p className="mt-2 text-gray-700 leading-relaxed break-words whitespace-pre-wrap">
                {task.teamRemarks}
              </p>
            </div>
          )}

          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-widest">Admin Instructions </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-2 w-full p-4 border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-50 focus:border-blue-400 focus:outline-none min-h-[140px] transition-all"
              placeholder="Enter detailed instructions for the client..."
            />
          </div>

          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3 block">Task Assets / Documents</label>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 hover:border-blue-300 transition-all relative cursor-pointer group">
                  <VscCloudUpload className="text-blue-400 group-hover:text-blue-500 text-4xl mb-2 transition-colors" />
                  <p className="text-gray-400 text-sm font-medium">Upload new files for the client</p>
                  <input type="file" multiple onChange={handleUpload} className="absolute inset-0 opacity-0 cursor-pointer" />
                </div>

                <div className="space-y-3 max-h-[200px] overflow-y-auto pr-2 custom-scrollbar">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="flex flex-col p-3 border border-gray-100 rounded-lg bg-white shadow-sm hover:shadow-md transition-all">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-600 text-xs font-medium truncate w-[60%]">{file.name}</span>
                        <div className="flex gap-3">
                          <a href={file.url} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-blue-500"><VscCloudDownload size={18} /></a>
                          <button onClick={() => { setPreviewUrl(file.url); setFileType(file.url.split('.').pop().toLowerCase()); setPreviewVisible(true); }} className="text-gray-400 hover:text-green-500"><AiOutlineEye size={18} /></button>
                          <button onClick={() => handleDelete(index)} className="text-gray-300 hover:text-red-500"><VscTrash size={18} /></button>
                        </div>
                      </div>
                      {task.documents?.[index]?.uploadedBy && (
                        <div className="mt-1 flex items-center gap-1">
                          <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                            {task.documents[index].uploadedBy}: {task.documents[index].uploadedByName || "Admin"}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
            </div>
          </div>

       

          <div className="flex justify-end pt-6 border-t border-gray-50">
            <button 
                onClick={handleSubmit} 
                className="px-10 py-3 rounded-full text-white bg-blue-600 hover:bg-blue-700 font-bold shadow-lg shadow-blue-200 transition-all transform active:scale-95 hover:-translate-y-1"
            >
              Update Task 
            </button>
          </div>
        </div>
      </div>

      <Modal title="Document Preview" open={previewVisible} onCancel={() => setPreviewVisible(false)} footer={null} width={1000} centered className="preview-modal">
        <div className="bg-gray-100 p-2 rounded-lg overflow-hidden">
          {["jpg", "jpeg", "png", "gif", "webp"].includes(fileType) ? (
            <img src={previewUrl} alt="Preview" className="w-full h-auto max-h-[80vh] object-contain rounded-md shadow-lg" />
          ) : (
            <iframe src={previewUrl} title="Preview" width="100%" height="700px" className="border-0 rounded-md shadow-lg bg-white" />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default ViewTaskHome;